<template>
  <section class="ai-section ai-settings-grid">
    <div class="ai-panel">
      <div class="ai-panel__header">
        <div>
          <h2 class="ai-panel__title">Provider credentials</h2>
          <p class="ai-panel__subtitle">Cheile rămân per persoană și nu ajung niciodată în frontend după salvare.</p>
        </div>
        <v-btn icon variant="text" size="small" :loading="settingsLoading" @click="refreshPanel">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <div v-if="settingsLoading" class="ai-loading">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="!settings.providers?.length" class="ai-empty">
        Nu există provideri disponibili.
      </div>
      <div v-else class="ai-settings-list">
        <article v-for="provider in settings.providers" :key="provider.provider_key" class="ai-settings-card">
          <div class="ai-settings-card__header">
            <div>
              <h3 class="ai-settings-card__title">{{ provider.provider_label }}</h3>
              <p class="ai-settings-card__subtitle">{{ provider.provider_key }}</p>
            </div>
            <v-chip :color="providerHealth(provider).color">
              {{ providerHealth(provider).chip }}
            </v-chip>
          </div>

          <div class="ai-provider-health" :class="providerHealth(provider).className">
            <v-icon class="ai-provider-health__icon">{{ providerHealth(provider).icon }}</v-icon>
            <div class="ai-provider-health__body">
              <strong>{{ providerHealth(provider).title }}</strong>
              <span>{{ providerHealth(provider).description }}</span>
              <small v-if="formatProviderTestLine(provider)">{{ formatProviderTestLine(provider) }}</small>
            </div>
          </div>

          <div class="ai-provider-usage">
            <v-chip size="x-small" color="neutral">
              {{ provider.configured ? "Cheie salvată criptat" : "Cheie nesalvată" }}
            </v-chip>
            <v-chip size="x-small" color="neutral">
              Per persoană
            </v-chip>
            <v-chip
              v-if="providerProfileSummary(provider.provider_key)"
              size="x-small"
              color="secondary"
            >
              {{ providerProfileSummary(provider.provider_key) }}
            </v-chip>
          </div>

          <v-text-field
            v-model="providerDrafts[provider.provider_key].label"
            label="Etichetă"
            hide-details
            class="mb-3"
          />
          <v-text-field
            v-model="providerDrafts[provider.provider_key].apiKey"
            :label="providerApiKeyLabel(provider)"
            type="password"
            hide-details
            class="mb-3"
          />
          <v-select
            v-model="providerDrafts[provider.provider_key].status"
            :items="providerStatusOptions"
            item-title="label"
            item-value="value"
            label="Status"
            hide-details
          />

          <div class="ai-settings-card__meta">
            <span>{{ providerKeyHint(provider) }}</span>
          </div>
          <p v-if="extractStoredProviderError(provider)" class="ai-settings-card__error">
            {{ extractStoredProviderError(provider) }}
          </p>

          <div class="ai-action-row">
            <v-btn
              color="primary"
              :disabled="providerRequiresApiKeyReplacement(provider)"
              :loading="providerSaveKey === provider.provider_key"
              @click="saveProvider(provider.provider_key)"
            >
              Salvează
            </v-btn>
            <v-btn
              variant="tonal"
              color="primary"
              :disabled="!provider.configured && !providerDrafts[provider.provider_key].apiKey"
              :loading="providerTestKey === provider.provider_key"
              @click="testProvider(provider.provider_key)"
            >
              Testează
            </v-btn>
          </div>
        </article>
      </div>
    </div>

    <div class="ai-panel">
      <div class="ai-panel__header">
        <div>
          <h2 class="ai-panel__title">Capability profiles</h2>
          <p class="ai-panel__subtitle">Profilele controlează providerul, modelul și modul de execuție.</p>
        </div>
      </div>

      <div v-if="settingsLoading" class="ai-loading">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="!settings.profiles?.length" class="ai-empty">
        Nu există profile AI încărcate.
      </div>
      <div v-else class="ai-settings-list">
        <article v-for="profile in settings.profiles" :key="profile.profile_key" class="ai-settings-card">
          <div class="ai-settings-card__header">
            <div>
              <h3 class="ai-settings-card__title">{{ profile.display_name }}</h3>
              <p class="ai-settings-card__subtitle">{{ profile.profile_key }}</p>
            </div>
            <v-chip :color="profile.is_active ? 'success' : 'grey'">
              {{ profile.is_active ? "activ" : "inactiv" }}
            </v-chip>
          </div>

          <v-text-field
            v-model="profileDrafts[profile.profile_key].provider_key"
            label="Provider"
            hide-details
            class="mb-3"
          />
          <v-text-field
            v-model="profileDrafts[profile.profile_key].model_key"
            label="Model"
            hide-details
            class="mb-3"
          />
          <div v-if="isOpenAiProfile(profile)" class="ai-model-policy mb-3">
            <v-icon color="tertiary">mdi-auto-fix</v-icon>
            <div class="ai-model-policy__body">
              <strong>{{ profile.model_policy_label || "OpenAI model" }}</strong>
              <span>{{ openAiModelPolicyText(profile) }}</span>
            </div>
            <v-chip color="tertiary">
              {{ profileDrafts[profile.profile_key].model_key || openAiLatestModel }}
            </v-chip>
          </div>
          <v-select
            v-model="profileDrafts[profile.profile_key].sync_mode"
            :items="syncModeOptions"
            item-title="label"
            item-value="value"
            label="Sync mode"
            hide-details
            class="mb-3"
          />
          <v-select
            v-model="profileDrafts[profile.profile_key].reasoning_effort"
            :items="reasoningOptions"
            item-title="label"
            item-value="value"
            label="Reasoning"
            hide-details
            class="mb-3"
          />
          <v-switch
            v-model="profileDrafts[profile.profile_key].is_active"
            inset
            hide-details
            color="primary"
            label="Profil activ"
          />

          <div class="ai-action-row">
            <v-btn
              color="primary"
              :loading="profileSaveKey === profile.profile_key"
              @click="saveProfile(profile.profile_key)"
            >
              Salvează profilul
            </v-btn>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import {
  OPENAI_LATEST_MODEL_KEY,
  PROVIDER_STATUS_OPTIONS,
  SYNC_MODE_OPTIONS,
  REASONING_OPTIONS,
  buildProviderDrafts,
  buildProfileDrafts,
  isOpenAiProviderKey,
} from "./aiWorkspaceShared.mjs";
import {
  buildProviderTestErrorMessage,
  buildProviderTestSuccessMessage,
  extractStoredProviderError,
  formatProviderTestStatus,
  hasCredentialDecryptError,
} from "./aiSettingsErrors.mjs";
import { loadAiSettings, saveAiProfileSettings, saveAiProviderSettings, testAiProviderSettings } from "./aiSettingsApi.mjs";

export default {
  name: "AiSettingsPanel",
  emits: ["feedback"],
  data() {
    return {
      openAiLatestModel: OPENAI_LATEST_MODEL_KEY,
      providerStatusOptions: PROVIDER_STATUS_OPTIONS,
      syncModeOptions: SYNC_MODE_OPTIONS,
      reasoningOptions: REASONING_OPTIONS,
      settings: {
        providers: [],
        profiles: [],
      },
      settingsLoading: false,
      providerDrafts: {},
      providerSaveKey: "",
      providerTestKey: "",
      profileDrafts: {},
      profileSaveKey: "",
    };
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    formatProviderTestStatus,
    extractStoredProviderError,
    hasCredentialDecryptError,
    isOpenAiProfile(profile) {
      const draft = this.profileDrafts[profile.profile_key] || {};
      return isOpenAiProviderKey(draft.provider_key || profile.provider_key);
    },
    openAiModelPolicyText(profile) {
      const draft = this.profileDrafts[profile.profile_key] || {};
      const modelKey = draft.model_key || profile.model_key || this.openAiLatestModel;
      if (profile.model_policy_key === "explicit") {
        return `Rulează explicit ${modelKey}. Pentru cost tests poți seta aici variante ca gpt-5.4-nano.`;
      }
      return `Modelul folosit este ${this.openAiLatestModel}; poți păstra latest sau seta un model explicit pentru test.`;
    },
    providerHealth(provider) {
      const isConfigured = Boolean(provider?.configured);
      const isActive = provider?.status === "active";
      const testStatus = provider?.last_test_status || "unknown";
      const hasDecryptError = this.hasCredentialDecryptError(provider);

      if (!isConfigured) {
        return {
          chip: "Nesetat",
          color: "neutral",
          className: "ai-provider-health--missing",
          icon: "mdi-key-alert-outline",
          title: "Nu există cheie salvată",
          description: "Adaugă o cheie API pentru ca workflow-urile AI să poată rula cu acest provider.",
        };
      }
      if (!isActive) {
        return {
          chip: "Inactiv",
          color: "neutral",
          className: "ai-provider-health--inactive",
          icon: "mdi-pause-circle-outline",
          title: "Cheia este salvată, dar inactivă",
          description: "Providerul nu va fi folosit până când statusul nu revine pe Activ.",
        };
      }
      if (testStatus === "ok") {
        return {
          chip: "Gata",
          color: "success",
          className: "ai-provider-health--ready",
          icon: "mdi-check-decagram-outline",
          title: "Cheia este validă și gata de utilizare",
          description: "Ultimul test a reușit. Workflow-urile pot folosi acest provider.",
        };
      }
      if (testStatus === "error") {
        if (hasDecryptError) {
          return {
            chip: "Cheie blocată",
            color: "error",
            className: "ai-provider-health--error",
            icon: "mdi-key-remove-outline",
            title: "Cheia salvată nu mai poate fi citită",
            description:
              "Introdu din nou API key-ul și salvează providerul. Secretul criptat vechi nu poate fi recuperat cu cheia master curentă.",
          };
        }
        return {
          chip: "Verifică",
          color: "error",
          className: "ai-provider-health--error",
          icon: "mdi-alert-circle-outline",
          title: "Cheia există, dar ultimul test a eșuat",
          description: "Verifică cheia, accesul la model și limitele proiectului înainte de a rula workflow-uri.",
        };
      }
      return {
        chip: "Netestat",
        color: "warning",
        className: "ai-provider-health--unknown",
        icon: "mdi-shield-key-outline",
        title: "Cheia este salvată, dar trebuie testată",
        description: "Rulează testul ca să confirmi că cheia are acces la modelul configurat.",
      };
    },
    formatDateTime(value) {
      if (!value) {
        return "";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }
      const pad = (part) => String(part).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    formatProviderTestLine(provider) {
      if (!provider?.last_tested_at) {
        return "";
      }
      return `Ultimul test: ${this.formatProviderTestStatus(provider.last_test_status)} · ${this.formatDateTime(provider.last_tested_at)}`;
    },
    profilesForProvider(providerKey) {
      return (this.settings.profiles || []).filter((profile) => profile.provider_key === providerKey);
    },
    providerProfileSummary(providerKey) {
      const profiles = this.profilesForProvider(providerKey);
      if (!profiles.length) {
        return "";
      }
      const activeProfiles = profiles.filter((profile) => profile.is_active);
      const count = activeProfiles.length || profiles.length;
      return `${count} profil${count === 1 ? "" : "e"} AI`;
    },
    providerKeyHint(provider) {
      if (this.hasCredentialDecryptError(provider)) {
        return "Credentialul vechi nu poate fi citit. Introdu API key nouă ca să îl rescrii.";
      }
      return provider?.configured
        ? "Lasă câmpul API key gol dacă vrei să păstrezi cheia existentă."
        : "Cheia API este obligatorie la prima salvare.";
    },
    providerApiKeyLabel(provider) {
      if (this.hasCredentialDecryptError(provider)) {
        return "API key nouă obligatorie";
      }
      return provider?.configured ? "Înlocuiește API key (opțional)" : "API key";
    },
    providerRequiresApiKeyReplacement(provider) {
      return this.hasCredentialDecryptError(provider) && !this.providerDrafts[provider.provider_key]?.apiKey;
    },
    emitFeedback(type, message) {
      this.$emit("feedback", { type, message });
    },
    refreshPanel() {
      return this.loadSettings();
    },
    async loadSettings() {
      this.settingsLoading = true;
      try {
        const { data } = await loadAiSettings(this.$route.query);
        this.settings = {
          providers: Array.isArray(data?.providers) ? data.providers : [],
          profiles: Array.isArray(data?.profiles) ? data.profiles : [],
        };
        this.providerDrafts = buildProviderDrafts(this.settings.providers);
        this.profileDrafts = buildProfileDrafts(this.settings.profiles);
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-au putut încărca setările AI.");
      } finally {
        this.settingsLoading = false;
      }
    },
    async saveProvider(providerKey) {
      this.providerSaveKey = providerKey;
      try {
        const draft = this.providerDrafts[providerKey];
        await saveAiProviderSettings({ providerKey, draft, query: this.$route.query });
        this.emitFeedback("success", `Credentialul ${providerKey} a fost salvat.`);
        await this.loadSettings();
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || `Nu s-a putut salva providerul ${providerKey}.`);
      } finally {
        this.providerSaveKey = "";
      }
    },
    async testProvider(providerKey) {
      this.providerTestKey = providerKey;
      try {
        const { data } = await testAiProviderSettings({ providerKey, query: this.$route.query });
        this.emitFeedback("success", buildProviderTestSuccessMessage(providerKey, data));
      } catch (error) {
        this.emitFeedback("error", buildProviderTestErrorMessage(error, providerKey));
      } finally {
        this.providerTestKey = "";
        await this.loadSettings();
      }
    },
    async saveProfile(profileKey) {
      this.profileSaveKey = profileKey;
      try {
        const draft = { ...this.profileDrafts[profileKey] };
        await saveAiProfileSettings({ profileKey, draft, query: this.$route.query });
        this.emitFeedback("success", `Profilul ${profileKey} a fost actualizat.`);
        await this.loadSettings();
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || `Nu s-a putut salva profilul ${profileKey}.`);
      } finally {
        this.profileSaveKey = "";
      }
    },
  },
};
</script>
